/**
 * Status-specific surface styles (badges, stop types, alerts).
 *
 * Badge color strings intentionally use the legacy lgb-* / Tailwind palette
 * (which all resolve through palette tokens) so the rendered badges remain
 * pixel-identical to the pre-design-system implementation. Centralizing them
 * here keeps usage DRY and reusable via `StatusBadge`.
 */

import { badgeBase } from "./styles";

/** Draft / neutral status */
export const statusDraft = `${badgeBase} bg-lgb-grey-100 text-lgb-grey-600`;

/** New / warning status */
export const statusNew = `${badgeBase} bg-lgb-yellow-100 dark:bg-lgb-yellow-200 text-lgb-yellow-600`;

/** Scheduled — true blue (not brand purple) */
export const statusScheduled = `${badgeBase} bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100`;

/** In progress — brand purple */
export const statusInProgress = `${badgeBase} bg-lgb-blue-100 dark:bg-lgb-blue-200 text-lgb-blue-400 dark:text-lgb-blue-400`;

/** Cancelled — danger */
export const statusCancelled = `${badgeBase} bg-lgb-red-100 dark:bg-lgb-red-200 text-lgb-red-400`;

/** Completed — success */
export const statusCompleted = `${badgeBase} bg-lgb-green-100 dark:bg-lgb-green-200 text-lgb-green-400 dark:text-lgb-green-400`;

/** Pickup stop — purple scale */
export const statusPickup = `${badgeBase} bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300`;

/** Delivery stop — green scale */
export const statusDelivery = `${badgeBase} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300`;

/** Pickup + delivery — blue scale */
export const statusPickupDelivery = `${badgeBase} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300`;

/** Dot indicators (pair with status* badge styles) */
export const statusDotDraft = "bg-lgb-grey-600";
export const statusDotNew = "bg-lgb-yellow-600";
export const statusDotScheduled = "bg-blue-600 dark:bg-blue-400";
export const statusDotInProgress = "bg-lgb-blue-400";
export const statusDotCancelled = "bg-lgb-red-400";
export const statusDotCompleted = "bg-lgb-green-400";
export const statusDotPickup = "bg-purple-500";
export const statusDotDelivery = "bg-green-500";
export const statusDotPickupDelivery = "bg-blue-500";

/** Pickup route card highlight */
export const surfacePickupRoute =
  "bg-gradient-to-r from-brand-muted via-brand-subtle to-surface dark:from-brand-muted/80 dark:via-brand-muted/40 dark:to-surface-input border-l-4 border-brand text-text shadow-sm";

/** Delivery route card highlight */
export const surfaceDeliveryRoute =
  "bg-gradient-to-r from-success-muted via-success-muted/50 to-surface dark:from-success-muted/80 dark:via-success-muted/40 dark:to-surface-input border-l-4 border-success text-text shadow-sm";

/** Warning / pending route card highlight */
export const surfaceWarningRoute =
  "bg-gradient-to-r from-warning-muted via-warning-muted/50 to-surface dark:from-warning-muted/80 dark:via-warning-muted/40 dark:to-surface-input border-l-4 border-warning text-text shadow-sm";

/** Neutral route card highlight */
export const surfaceNeutralRoute =
  "bg-gradient-to-r from-surface-muted via-surface to-surface dark:from-surface-muted/80 dark:via-surface-muted/40 dark:to-surface-input border-l-4 border-border-strong text-text shadow-sm";


/** Link styled with brand color */
export const linkBrand =
  "text-brand hover:text-brand-hover transition-colors";

/** Focus ring matching brand */
export const focusBrand =
  "focus:border-brand focus:ring-brand focus:ring-1";

/** Checkbox / radio accent */
export const controlBrand =
  "checked:bg-brand focus:ring-brand dark:checked:bg-brand dark:focus:ring-brand";
